import { useState } from "react";
import { useInput } from "ra-core";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { DateInput } from "@/components/admin/date-input";
import { useWatch } from "react-hook-form";
import { FormControl, FormField, FormLabel } from "@/components/admin/form";
import { Input } from "@/components/ui/input";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { numeroBr } from "../seguros/cotacaoLogica";
import { MoedaFormInput } from "@/components/admin/moeda-input";

/**
 * Os campos do lead que também são campos da PESSOA.
 *
 * POR QUE existe um arquivo só para isto. O Rick pediu o formulário de "novo
 * lead" e completou: "ou seja, algo como o contato que já temos, seria bom
 * tentar fazer um módulo só para os dois". Duas telas pedindo renda, profissão
 * e patrimônio da mesma pessoa viram duas verdades sobre ela — e a segunda a
 * ser preenchida é a que ninguém preenche.
 *
 * Então estes grupos são UM componente, montado nos dois lugares:
 *   - na ficha do contato (`contacts/ContactInputs.tsx`), onde a pessoa é o
 *     assunto;
 *   - no formulário de novo lead (`NovoLeadDialog.tsx`), que cria a pessoa e o
 *     card do funil na mesma submissão.
 *
 * ONDE ISTO É GRAVADO. Em `contacts.details`, a coluna JSONB onde a ficha de
 * contato já guarda nome, CPF, telefone e endereço. Nenhuma migration: campo
 * novo aqui é chave nova no JSON, e quem não preencheu simplesmente não tem a
 * chave. O preço é não dar para filtrar por eles no banco sem um índice — se um
 * dia alguém quiser "todos os leads com renda acima de X", aí vale promover
 * aquele campo a coluna. Hoje ninguém quer, e 15 colunas vazias numa tabela
 * usada pelo app inteiro seriam pior.
 *
 * TUDO É CAIXA DE TEXTO, de propósito, como veio no pedido. "Renda", "cônjuge
 * e filhos" e "patrimônio" são respostas de conversa, não de formulário: quem
 * está ao telefone escreve "uns 8 mil, mais comissão" ou "esposa Ana e dois
 * filhos". Um campo numérico ou uma data obrigatória forçariam a pessoa a
 * inventar precisão que ela não tem, e o dado inventado é pior que o dado
 * solto — este é o mesmo raciocínio de `consortium/parcelasEmAberto.ts`.
 */

/**
 * Seguro auto e plano de saúde que a pessoa JÁ tem (reunião de 28/10/2026):
 * os dois renovam todo ano, e saber a seguradora e o vencimento vira
 * oportunidade — a rotina diária da migration 283 avisa o dono do contato 30
 * dias antes e abre o card de renovação no funil. O texto livre de antes
 * (`details.<prefixo>`) continua como "Detalhes".
 *
 * Gravado em contacts.details: `<prefixo>_has`, `<prefixo>_company`,
 * `<prefixo>_due` (AAAA-MM-DD).
 */
const SeguroQuePossui = ({
  prefixo,
  titulo,
  rotuloEmpresa,
}: {
  prefixo: "auto_insurance" | "health_plan";
  titulo: string;
  rotuloEmpresa: string;
}) => {
  const possui = useWatch({ name: `details.${prefixo}_has` });
  const textoAntigo = useWatch({ name: `details.${prefixo}` });
  return (
    <div className="flex flex-col gap-3 rounded-md border p-3">
      <BooleanInput source={`details.${prefixo}_has`} label={titulo} helperText={false} />
      {possui && (
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput source={`details.${prefixo}_company`} label={rotuloEmpresa} helperText={false} />
          <DateInput
            source={`details.${prefixo}_due`}
            label="Vencimento"
            helperText="Aviso de renovação 30 dias antes."
          />
        </div>
      )}
      {(possui || !!textoAntigo) && (
        <TextInput source={`details.${prefixo}`} label="Detalhes" multiline rows={2} helperText={false} />
      )}
    </div>
  );
};

/**
 * Do que é feito o patrimônio e onde está o investimento (reunião de
 * 28/10/2026): "5 milhões" sozinho não diz se é casa, terreno ou CDB, e é o
 * tipo que vira oportunidade (base de investidores, proteção de patrimônio).
 * O texto continua sendo a resposta principal; os tipos marcam o que a pessoa
 * citou. Gravado como lista em contacts.details.
 */
export const TIPOS_DE_PATRIMONIO = [
  "Imóvel residencial",
  "Imóvel de veraneio",
  "Terreno",
  "Imóvel comercial",
  "Imóvel rural",
  "Veículos",
  "Empresa ou participação",
];

export const TIPOS_DE_INVESTIMENTO = [
  "Previdência",
  "Renda fixa (CDB, LCI, Tesouro)",
  "Fundos",
  "Ações",
  "Criptoativos",
  "Investimento no exterior",
  "Poupança",
];

const TiposMarcados = ({ source, opcoes, rotulo }: { source: string; opcoes: string[]; rotulo: string }) => {
  const { field } = useInput({ source });
  const marcados: string[] = Array.isArray(field.value) ? field.value : [];
  const alternar = (op: string) =>
    field.onChange(marcados.includes(op) ? marcados.filter((m) => m !== op) : [...marcados, op]);
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs text-muted-foreground">{rotulo}</span>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={rotulo}>
        {opcoes.map((op) => (
          <button
            key={op}
            type="button"
            aria-pressed={marcados.includes(op)}
            onClick={() => alternar(op)}
            className={
              marcados.includes(op)
                ? "rounded-full border border-marinho bg-marinho px-2.5 py-1 text-xs font-medium text-white"
                : "rounded-full border px-2.5 py-1 text-xs text-foreground/80 hover:bg-muted"
            }
          >
            {op}
          </button>
        ))}
      </div>
    </div>
  );
};

/** O produto cujo lead abre a ADN (`crm/adn/`). Nome da configuração da casa. */
export const PRODUTO_VIDA = "Vida";

/**
 * Escolher Vida acrescenta os passos ADN e Resumo lá adiante na trilha, e só
 * isso passava despercebido (Rick: "não aparece nada quando seleciono vida").
 */
const AvisoDaAdn = () =>
  useWatch({ name: "produto" }) === PRODUTO_VIDA ? (
    <p className="rounded-md border border-ouro/50 bg-ouro/10 px-3 py-2 text-[13px]">
      ADN de seguro de vida incluída: passos <b>ADN</b> e <b>Resumo</b>, com o capital necessário calculado.
    </p>
  ) : null;

/** Nome do produto que o lead está negociando. Vem da configuração da casa. */
export const useProdutosDoLead = () => useConfigurationContext().dealCategories;

/**
 * Cada pergunta da ficha, UMA vez. Os grupos abaixo (ficha de contato em uma
 * tela só) e os passos da jornada de cadastro (`contacts/passosDaFicha.tsx`,
 * poucos campos por vez) montam a partir daqui — mudar um rótulo ou uma
 * validação num lugar e esquecer o outro é justamente o que este arquivo
 * existe para impedir.
 */
export const CAMPOS_DA_PESSOA = {
  profissao: <TextInput source="details.profession" label="Profissão" helperText={false} />,
  briefing: (
    <TextInput
      source="details.background"
      label="Briefing do cliente"
      multiline
      rows={3}
      helperText="Resumo da conversa: objetivos, o que já possui e principais preocupações."
    />
  ),
  nomesDaFamilia: (
    <TextInput
      source="details.family_names"
      label="Nome do cônjuge e dos filhos"
      multiline
      rows={2}
      helperText={false}
    />
  ),
  nascimentosDaFamilia: (
    <TextInput
      source="details.family_birth_dates"
      label="Datas de nascimento do cônjuge e dos filhos"
      multiline
      rows={2}
      helperText="Informe a data completa ou, se não for conhecida, apenas o ano."
    />
  ),
  rendaDoConjuge: <MoedaFormInput source="details.spouse_income" label="Renda do cônjuge" helperText={false} />,
  investimentos: (
    <div className="flex flex-col gap-2">
      <TextInput source="details.investments" label="Investimentos" multiline rows={2} helperText={false} />
      <TiposMarcados source="details.investments_types" opcoes={TIPOS_DE_INVESTIMENTO} rotulo="Onde está investido" />
    </div>
  ),
  patrimonio: (
    <div className="flex flex-col gap-2">
      <TextInput source="details.assets" label="Patrimônio" multiline rows={2} helperText={false} />
      <TiposMarcados source="details.assets_types" opcoes={TIPOS_DE_PATRIMONIO} rotulo="Do que é o patrimônio" />
    </div>
  ),
  seguroDeVida: <TextInput source="details.life_insurance" label="Seguro de vida" helperText={false} />,
  seguroAuto: <SeguroQuePossui prefixo="auto_insurance" titulo="Possui seguro auto" rotuloEmpresa="Seguradora" />,
  planoDeSaude: <SeguroQuePossui prefixo="health_plan" titulo="Possui plano de saúde" rotuloEmpresa="Operadora" />,
  financiamentos: (
    <TextInput source="details.financing" label="Financiamentos" multiline rows={2} helperText={false} />
  ),
  emprestimos: <TextInput source="details.loans" label="Empréstimos" multiline rows={2} helperText={false} />,
  outrasInformacoes: (
    <TextInput
      source="details.other_info"
      label="Outras informações"
      multiline
      rows={3}
      helperText={false}
    />
  ),
};

/**
 * Perfil profissional e briefing. Complementa o bloco de identificação que a
 * ficha de contato já tem (nome, CPF, nascimento, renda) em vez de repeti-lo.
 */
export const CamposDePerfil = () => (
  <div className="flex flex-col gap-4">
    <h3 className="text-sm font-medium text-muted-foreground">Perfil</h3>
    {CAMPOS_DA_PESSOA.profissao}
    {CAMPOS_DA_PESSOA.briefing}
  </div>
);

/**
 * Número à brasileira (vírgula decimal), ligado ao react-hook-form via
 * `useInput`. Não é `NumberInput` de `components/admin`: aquele formata como
 * centavos de moeda a cada tecla (é para "renda", "valor negociado" — nunca
 * `1,78`) e reformatar em cima do que a pessoa ainda está digitando apaga a
 * vírgula. Mesma técnica de `seguros/CotacaoPage.tsx` (`CampoNumero`): um
 * rascunho local vale enquanto o campo está em foco, e só ao sair o valor
 * volta a ser o número formatado.
 */
const CampoDecimalBr = ({ source, label }: { source: string; label: string }) => {
  const { id, field } = useInput({ source });
  const [rascunho, setRascunho] = useState<string | null>(null);
  const valorNumerico = typeof field.value === "number" ? field.value : Number(field.value) || 0;
  const texto = rascunho ?? (valorNumerico ? String(valorNumerico).replace(".", ",") : "");

  return (
    <FormField id={id} name={field.name}>
      <FormLabel>{label}</FormLabel>
      <FormControl>
        <Input
          id={id}
          inputMode="decimal"
          value={texto}
          onChange={(e) => {
            setRascunho(e.target.value);
            field.onChange(numeroBr(e.target.value) ?? 0);
          }}
          onBlur={() => {
            setRascunho(null);
            field.onBlur();
          }}
        />
      </FormControl>
    </FormField>
  );
};

/**
 * Altura, peso e fumante — os ÚNICOS campos deste arquivo que não são caixa de
 * texto, e de propósito contrário ao resto: a jornada de cotação de seguros
 * (`seguros/cotacao/`) manda esses valores direto para a API da Azos, que
 * exige número em metros/quilos e boolean, não "1,78" solto num texto livre.
 * Ficam editáveis aqui pelo mesmo motivo dos outros grupos — para não haver
 * duas fichas com respostas diferentes — mas quem os preenche primeiro,
 * hoje, normalmente é a própria cotação, não a ficha do contato.
 */
export const CamposDeSaudeParaSeguro = () => (
  <div className="flex flex-col gap-4">
    <h3 className="text-sm font-medium text-muted-foreground">Perfil de risco (seguro de vida)</h3>
    <MedidasParaSeguro />
  </div>
);

/** Os campos de `CamposDeSaudeParaSeguro` sem o título, para a jornada. */
export const MedidasParaSeguro = () => (
  <>
    <div className="flex gap-4">
      <CampoDecimalBr source="details.height_m" label="Altura (m)" />
      <CampoDecimalBr source="details.weight_kg" label="Peso (kg)" />
    </div>
    <BooleanInput source="details.smoker" label="Fumante" helperText={false} />
  </>
);

/**
 * Cônjuge e filhos num campo de texto só, porque é assim que a informação
 * chega. Uma lista estruturada de dependentes seria mais bonita e ficaria
 * vazia: ninguém para a ligação para cadastrar três pessoas uma a uma.
 */
export const CamposDaFamilia = () => (
  <div className="flex flex-col gap-4">
    <h3 className="text-sm font-medium text-muted-foreground">Cônjuge e família</h3>
    {CAMPOS_DA_PESSOA.nomesDaFamilia}
    {CAMPOS_DA_PESSOA.nascimentosDaFamilia}
    {CAMPOS_DA_PESSOA.rendaDoConjuge}
  </div>
);

/**
 * O que a pessoa já tem. É daqui que sai a conversa de proteção: quem tem
 * patrimônio e não tem seguro de vida é uma frase, não uma planilha.
 */
export const CamposDePatrimonio = () => (
  <div className="flex flex-col gap-4">
    <h3 className="text-sm font-medium text-muted-foreground">Patrimônio</h3>
    {CAMPOS_DA_PESSOA.investimentos}
    {CAMPOS_DA_PESSOA.patrimonio}
    {CAMPOS_DA_PESSOA.seguroDeVida}
    {CAMPOS_DA_PESSOA.seguroAuto}
    {CAMPOS_DA_PESSOA.planoDeSaude}
  </div>
);

/** O que a pessoa deve. O outro lado da mesma conversa. */
export const CamposDeInventario = () => (
  <div className="flex flex-col gap-4">
    <h3 className="text-sm font-medium text-muted-foreground">Inventário</h3>
    {CAMPOS_DA_PESSOA.financiamentos}
    {CAMPOS_DA_PESSOA.emprestimos}
    {CAMPOS_DA_PESSOA.outrasInformacoes}
  </div>
);

/**
 * Produto e valor negociado. Estes dois NÃO são da pessoa, são do negócio:
 * a mesma pessoa pode ter um consórcio e um seguro de vida em andamento, cada
 * um com seu valor e sua coluna no funil. Por isso ficam fora dos grupos acima
 * e não aparecem na ficha do contato.
 */
export const CamposDoNegocio = ({ comTitulo = true }: { comTitulo?: boolean }) => {
  const produtos = useProdutosDoLead();
  return (
    <div className="flex flex-col gap-4">
      {comTitulo && <h3 className="text-sm font-medium text-muted-foreground">Negócio</h3>}
      <SelectInput
        source="produto"
        label="Produto"
        choices={produtos.map((p) => ({ id: p, name: p }))}
        helperText={false}
      />
      <AvisoDaAdn />
      <MoedaFormInput
        source="valor_negociado"
        label="Valor negociado"
        helperText="Opcional enquanto não houver valor definido."
      />
    </div>
  );
};

/**
 * Tudo que a ficha da PESSOA ganha por causa do CRM, numa peça só.
 * É este componente que a ficha de contato monta.
 */
export const CamposDePerfilCompleto = () => (
  <div className="flex flex-col gap-8">
    <CamposDePerfil />
    <CamposDeSaudeParaSeguro />
    <CamposDaFamilia />
    <CamposDePatrimonio />
    <CamposDeInventario />
  </div>
);
